import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

type SubmitButtonProps = React.ButtonHTMLAttributes<HTMLButtonElement> & {
    loading?: boolean;
    loadingText?: string;
    variant?: "primary" | "secondary" | "outline";
    fullWidth?: boolean;
};

const VARIANT_CLASSES: Record<NonNullable<SubmitButtonProps["variant"]>, string> = {
    primary: "bg-brand text-white hover:bg-brand-600 shadow-brand hover:shadow-elevated hover:-translate-y-0.5",
    secondary: "bg-foreground text-white hover:bg-foreground/90",
    outline: "border border-border bg-background text-foreground hover:border-brand/40 hover:text-brand",
};

/**
 * SubmitButton — themed CTA with built-in loading state.
 *
 * • Shows a spinner + optional `loadingText` while `loading` is true.
 * • Disables interaction and dims content automatically.
 * • Composes on top of the shadcn Button so keyboard focus & size tokens
 *   remain consistent.
 */
export function SubmitButton({
    loading = false,
    loadingText,
    variant = "primary",
    fullWidth = true,
    children,
    disabled,
    className,
    ...props
}: SubmitButtonProps) {
    return (
        <Button
            {...props}
            disabled={loading || disabled}
            className={cn(
                "h-11 rounded-full text-sm font-semibold transition-all",
                VARIANT_CLASSES[variant],
                fullWidth && "w-full",
                className,
            )}
        >
            {loading ? (
                <span className="inline-flex items-center gap-2">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    {loadingText ?? "Please wait…"}
                </span>
            ) : (
                children
            )}
        </Button>
    );
}

export default SubmitButton;
